import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

// KISS revision (2026-08-09): the visible + spoken login greeting is
// gated by a one-shot sessionStorage flag with three states:
//   • absent       → no greeting to deliver
//   • "pending"    → a fresh login just occurred; AriaContext must greet
//   • "done"       → greeting has been delivered (or attempted); no repeat
// Set to "pending" at the moment a fresh login completes (post-password
// or post-2FA). Signup does NOT use this flow — a first-time user must
// not hear "Welcome back". AriaContext flips "pending" → "done"
// atomically before showing the visible toast so a StrictMode double
// invocation or a provider remount cannot re-greet. Logout removes the
// flag entirely so the next successful login starts a fresh "pending".
const ARIA_GREET_PENDING_KEY = "aria_greet_pending";
function _setGreetPending() {
  try { sessionStorage.setItem(ARIA_GREET_PENDING_KEY, "pending"); } catch { /* ignore */ }
}
function _clearGreetPending() {
  try { sessionStorage.removeItem(ARIA_GREET_PENDING_KEY); } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pa_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("pa_token");
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * login(email, password, opts?)
   * opts: { captcha_token, captcha_answer }
   * Returns:
   *   { requires_2fa: true, challenge_token }
   *   OR { ...user, requires_enrollment }
   */
  const login = async (email, password, opts = {}) => {
    const r = await api.post("/auth/login", {
      email,
      password,
      captcha_token: opts.captcha_token,
      captcha_answer: opts.captcha_answer,
    });
    if (r.data.requires_2fa) {
      return { requires_2fa: true, challenge_token: r.data.challenge_token };
    }
    localStorage.setItem("pa_token", r.data.token);
    // KISS revision (2026-08-09) — atomic lifecycle: mark greet as
    // "pending" BEFORE setUser so the AriaContext effect sees the flag on
    // the same render it first sees the authenticated user.
    _setGreetPending();
    setUser(r.data.user);
    return { ...r.data.user, requires_enrollment: !!r.data.requires_enrollment };
  };

  const completeTwoFactor = async (challenge_token, code) => {
    const r = await api.post("/auth/login-2fa", { challenge_token, code });
    localStorage.setItem("pa_token", r.data.token);
    const me = await api.get("/auth/me");
    _setGreetPending();
    setUser(me.data);
    return { user: me.data, used_recovery: r.data.used_recovery, recovery_remaining: r.data.recovery_codes_remaining };
  };

  const signup = async (email, password, full_name, baa_accepted) => {
    // Signup is intentionally OUTSIDE the "Welcome back" flow — a first-
    // time user must not hear a returning-user greeting.
    const r = await api.post("/auth/signup", {
      email,
      password,
      full_name,
      baa_accepted: !!baa_accepted,
    });
    localStorage.setItem("pa_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("pa_token");
    _clearGreetPending();
    setUser(null);
  };

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (_) {}
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, completeTwoFactor, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
